import type { TeamGameResponse } from '@dto/model/models';
import { BoxExtendedProps } from 'grommet';
import { HeightType } from 'grommet/utils';
import type { shd } from '@shd/jslib/models';
import type { AppHostMode } from '@services/appHost';
import { appWeb } from '@shd/jslib/ui';
import { CameraStatus } from '@features/SidelineLive/sidelineLive.types';
import type {
  BaseballLiveGame,
  BasicGameData,
  VolleyballGameData,
  TeamGameLiveResponse,
} from '@dto/api';

export enum ScheduleStatus {
  LIVE = 'Live',
  TODAY = 'Today',
  UPCOMING = 'Upcoming',
  PAST = 'Past',
  ISCORE = 'iScore',
}

export interface ScheduleCardListProps {
  scheduleStatus: ScheduleStatus;
  games?: TeamGameResponse[];
  viewportSize?: string;
  handlePropUpdates?: () => BoxExtendedProps;
  isIScore?: boolean;
  isAdmin?: boolean;
  teamId: string;
  teamAttribSportType?: string;
  listRef: React.RefObject<HTMLDivElement> | undefined;
  height?: HeightType;
  onClick?: (status: ScheduleStatus, game: TeamGameResponse) => void;
}

export interface LiveScoringStatusHookResponse {
  baseball: BaseballLiveGame | undefined;
  volleyball: VolleyballGameData | undefined;
  otherSport: BasicGameData | undefined;
  gameStatus: TeamGameLiveResponse | undefined;
}

export interface ScheduleCardProps {
  scheduleStatus: ScheduleStatus;
  gameInfo: TeamGameResponse;
  viewportSize?: string;
  teamId?: string;
  liveScoringStatus?: LiveScoringStatusHookResponse;
  onClick?: (status: ScheduleStatus, game: TeamGameResponse) => void;
}

interface ScheduleProps {
  teamId: string;
  teamName: string;
  viewportSize?: string;
  isIScore?: boolean;
  isAdmin?: boolean;
  isScoringLink?: boolean;
  hasRoster: boolean;
  teamAttribSportType?: string;
  sportType?: string;
  headerHeight: number | null;
}

interface UpcomingCardListProps {
  scheduleStatus: ScheduleStatus;
  games?: TeamGameResponse[];
  todayGames?: TeamGameResponse[];
  upcomingGames?: TeamGameResponse[];
  pastGames?: TeamGameResponse[];
  viewportSize?: string;
  teamId: string;
  onClick?: (status: ScheduleStatus, game: TeamGameResponse) => void;
  handlePropUpdates?: () => BoxExtendedProps;
  upcomingListRef: React.RefObject<HTMLDivElement> | undefined;
  todayListRef: React.RefObject<HTMLDivElement> | undefined;
  pastListRef: React.RefObject<HTMLDivElement> | undefined;
  isIScore: boolean;
  isAdmin: boolean;
  isScoringLink: boolean;
}

interface LiveCardProps {
  teamId: string;
  gameInfo?: TeamGameResponse | null;
  viewportSize?: string;
  cameraStatus?: CameraStatus;
  isPlayable?: boolean;
  gameIsStreaming?: boolean;
  onClick?: (status: ScheduleStatus, game: TeamGameResponse) => void;
  hlsRef?: React.RefObject<string | undefined>;
  scheduleStatus: ScheduleStatus;
}

export interface ScoringTrayProps {
  teamId: string;
  sportType: string;
  onClose: () => void;
  gameInfo: TeamGameResponse;
}

export interface GameActionBarProps {
  teamId: string;
  teamName: string;
  sportType: string;
  hasRoster: boolean;
  isIScore: boolean;
}

export interface CreateNewGameProps {
  game?: TeamGameResponse;
  mode?: 'create' | 'edit';
  onClose: (abandoned: boolean) => void;
  teamId: string;
  sportType: string;
}

export interface TeamGame {
  DNOR_teamId: string;
  createdBecause: string;
  createdBy: string;
  createdTs: number;
  date: string;
  id: string;
  modifiedTs: number;
  modifiedUser: string;
  paths?: {
    details?: string;
    team?: string;
    live_game?: string;
  };
  scheduleTsStart: number;
  scoreGameId: string;
  scoreGameState: number;
  scoreInningNum: number;
  scoreOpponentName: string;
  scoreOurLineup: string[];
  scoreOurName: string;
  scoreWeAreHome: number;
  scoresAwayHome: number[];
  time: string;
  title: string;
}

export enum GameStatus {
  UNKNOWN = 0,
  SCHEDULED = 10,
  SCHEDULED_AND_BROADCAST_OR_SCORING = 20,
  STARTED = 30,
  TIMEOUT = 40,
  ENDED = 50,
}

export enum GameActions {
  GO_LIVE = 'Go live',
  START_SCORING = 'Start scoring',
  SET_LINEUP = 'Set lineup',
  END_GAME = 'End game',
  RESUME_SCORING = 'Resume scoring',
  EDIT_GAME = 'Edit game',
}

export interface DeleteGameModalProps {
  body?: string;
  onClose: () => void;
  onDeleteGame: () => void;
}

export interface NoScoringKeyTrayProps {
  onClose: () => void;
}

export interface GameActionSheetProps {
  game: TeamGameResponse;
  status: GameStatus;
  onClick: (action: GameActions, game: TeamGameResponse) => void;
  isScoringLink?: boolean;
}

export enum GameStartEvent {
  ADD_GAME_CLICKED = 'add_game_clicked',
  GAME_CREATION_COMPLETED = 'game_creation_completed',
  GAME_CREATION_ABANDONED = 'game_creation_abandoned',
  GAME_CARD_CLICKED = 'game_card_clicked',
  GAME_ACTION_SHEET_OPENED = 'game_action_sheet_opened',
  GAME_ACTION_SELECTED = 'game_action_selected',
  PAIR_ISCORE_CLICKED = 'pair_iscore_clicked',
  UNPAIR_ISOCRE_CLICKED = 'unpair_iscore_clicked',
  ISCORE_SYNC_CLICKED = 'iscore_sync_clicked',
  GO_LIVE_BUTTON_CLICKED = 'go_live_button_clicked',
}

export enum GameActionTaken {
  EDIT_GAME = 'edit_game',
  GO_LIVE = 'go_live',
  START_SCORING = 'start_scoring',
  SET_LINEUP = 'set_lineup',
  END_GAME = 'end_game',
}

export enum GameActionUserType {
  ADMIN = 'admin',
  COMMUNITY_MEMBER = 'community_member',
  LOGGED_OUT = 'loggedOut',
  LOGGED_IN = 'loggedIn',
}

export interface CameraSelectionSheetProps {
  teamId: string;
  teamName: string;
  game?: TeamGameResponse;
  onClick: (type: 'settings' | 'go_live', game?: TeamGameResponse) => void;
  onCloseTray: () => void;
}

export interface EnableCameraModalProps {
  capture: shd.TeamCapture;
  teamId: string;
  teamName: string;
  isOtherTeam: boolean;
  onUpdate: (capture: shd.TeamCapture) => void;
  onClose: () => void;
}

export interface UserCannotModalProps {
  heading: string;
  body: string;
  onClose: () => void;
}

export interface GoProModalProps {
  appHost: AppHostMode;
  appHostService: appWeb.AppHostService;
  capture: shd.TeamCapture;
  onClose: () => void;
}

export interface MevoModalProps extends GoProModalProps {}
